import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import InnerLeadBanner from '../components/InnerLeadBanner';

const Apply = () => {
  const { id } = useParams();

  const isPwDRef = useRef(null);
  const disabilityTypeRef = useRef(null);
  const otherDisabilityRef = useRef(null);
  const nameRef = useRef(); // Still useful if you want to focus on it for error
  const formRef = useRef();

  const [showDisabilityOptions, setShowDisabilityOptions] = useState(false);
  const [showOtherField, setShowOtherField] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedCVPath, setUploadedCVPath] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [cvUploadMessage, setCvUploadMessage] = useState('');

  const handlePwDChange = () => {
    const value = isPwDRef.current.value;
    setShowDisabilityOptions(value === 'yes');
    setShowOtherField(false);
  };

  const handleDisabilityChange = () => {
    const value = disabilityTypeRef.current.value;
    setShowOtherField(value === 'other');
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setCvUploadMessage('');
      setUploadProgress(0);
      setUploadedCVPath(''); // Clear path if file is unselected
      return;
    }

    setCvFile(file);
    setSubmissionError(null);
    setCvUploadMessage('');
    setUploadProgress(0);
    setUploadedCVPath(''); // Clear previous path before new upload

    const formData = new FormData();
    formData.append('cv', file);
    formData.append('opportunity_pk', id); // Include the opportunity ID in the form data

    const xhr = new XMLHttpRequest();
    const url = 'https://backend.jobflowonline.com/upload-cv';
    // const url = 'http://localhost:8000/upload-cv';
    xhr.open('POST', url, true);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        let percentCompleted = Math.round((event.loaded * 100) / event.total);
        setUploadProgress(percentCompleted);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        try {
          const data = JSON.parse(xhr.responseText);
          setUploadedCVPath(data.file_url);
          setCvUploadMessage('CV uploaded successfully ✅');
          setUploadProgress(100);
        } catch (error) {
          console.error('CV Upload Parse Error:', error);
          setSubmissionError('Failed to process CV upload response.');
          setCvUploadMessage('CV upload failed: Invalid response from server.');
          setUploadProgress(0);
        }
      } else {
        console.error('CV Upload Error:', xhr.status, xhr.statusText);
        setSubmissionError('Failed to upload CV. Please try again.');
        setCvUploadMessage(`CV upload failed: ${xhr.status} ${xhr.statusText || 'Unknown error'}`);
        setUploadProgress(0);
      }
    };

    xhr.onerror = () => {
      console.error('CV Upload Network Error');
      setSubmissionError('Network error during CV upload. Please check your connection.');
      setCvUploadMessage('CV upload failed: Network error.');
      setUploadProgress(0);
    };

    xhr.send(formData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // This line prevents default HTML5 validation
    setSubmissionError(null); // Clear any previous errors

    const form = formRef.current;
    const formData = new FormData(form);

    // PwD specific validation
    const isPwD = formData.get('isPwD');
    if (isPwD === 'yes') {
      const disabilityType = formData.get('disabilityType');
      if (!disabilityType || disabilityType.trim() === '') {
        setSubmissionError('Please select your type of disability.');
        disabilityTypeRef.current.focus();
        return;
      }
      if (disabilityType === 'other') {
        const otherDisability = formData.get('otherDisability');
        if (!otherDisability || otherDisability.trim() === '') {
          setSubmissionError('Please specify your disability type.');
          otherDisabilityRef.current.focus();
          return;
        }
      }
    }

    // CV upload validation
    if (!uploadedCVPath && cvFile) {
      setSubmissionError('Please wait for your CV to finish uploading before submitting.');
      return;
    }
    if (!uploadedCVPath && !cvFile) {
      setSubmissionError('Please upload your CV.');
      return;
    }
    // --- End Client-Side Validation ---


    // Collect individual fields (same as before, but using formData)
    // console.log('Form Data:', Array.from(formData.entries())); // Debugging line to see all form data
    const fullname = formData.get('fullname');
    const age = formData.get('age');
    const phone = formData.get('phone');
    const gender = formData.get('gender');
    const email = formData.get('email');
    const country = formData.get('country');
    const district = formData.get('district');
    const community = formData.get('community');
    const aboutYourself = formData.get('aboutYourself');
    const disabilityType = formData.get('disabilityType');
    const otherDisability = formData.get('otherDisability');
    const educationLevel = formData.get('education_level');
    const opportunity_pk = id;
    // const fluentLanguages = formData.getAll('fluentLanguages'); // Commented out in your version
    // const availableDays = formData.getAll('availableDays'); // Commented out in your version

    // Prepare final payload (include uploadedCVPath)
    const submissionData = {
      fullname,
      age,
      phone,
      gender,
      email,
      country,
      district,
      community,
      aboutYourself,
      isPwD,
      opportunity_pk, // Include the opportunity ID in the submission

      // Correctly set disabilityType based on whether "other" was selected
      disabilityType: isPwD === 'yes' && disabilityType === 'other' ? otherDisability : disabilityType,
      educationLevel,
      // fluentLanguages,
      // availableDays,
      cv: uploadedCVPath, // Include the path, not the File object.
    };

    // Send as JSON
    try {
        const url = 'https://backend.jobflowonline.com/apply';
        // const url = 'http://localhost:8000/apply';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // Tell the server we're sending JSON
          // If you have CSRF protection:
          // 'X-CSRFToken': getCookie('csrftoken'),  //  Implement getCookie
        },
        body: JSON.stringify(submissionData), // Send the JSON
    });
    console.log('Submission Data:', submissionData) // Debugging line to see the final payload

      if (!response.ok) {
        const errorData = await response.json(); // Try to parse error message from backend
        throw new Error(`Application submission failed: ${response.status} - ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      console.log('Success:', data);
      alert('Application submitted successfully!'); // Simple success message
      form.reset(); // Clear the form
      setShowDisabilityOptions(false); // Reset UI states
      setShowOtherField(false);
      setUploadedCVPath('');
      setCvFile(null);
      setUploadProgress(0);
      setCvUploadMessage('');
      window.location = '/'; // Redirect to home page
    } catch (error) {
      console.error('Submission Error:', error);
      setSubmissionError('Failed to submit application. Please check your information and try again. ' + error.message);
    }
  };

  return (
    <>
      <Navbar />
      <InnerLeadBanner title="Apply Now" />

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="contact-title">Apply Now</h2>
            </div>
            <div className="col-lg-8">
              <form
                ref={formRef}
                className="form-contact contact_form"
                onSubmit={handleSubmit}
                method="post"
                id="contactForm"
                noValidate="noValidate" // Keep noValidate to prevent browser's default validation
              >
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <input
                        className="form-control valid"
                        name="fullname"
                        id="fullname"
                        type="text"
                        ref={nameRef}
                        placeholder="Enter your name"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <input
                        className="form-control valid"
                        name="age"
                        id="age"
                        type="number"
                        placeholder="Enter your age"
                    
                      />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <input
                        className="form-control valid"
                        name="phone"
                        id="phone"
                        type="tel"
                        placeholder="Phone number (WhatsApp preferred)"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <input
                        className="form-control valid"
                        name="email"
                        id="email"
                        type="email"
                        placeholder="Email"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <select className="form-control" name="gender" id="gender">
                        <option value="">Select your gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <input
                        className="form-control valid"
                        name="country"
                        id="country"
                        type="text"
                        placeholder="Country"
                        
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <input
                        className="form-control valid"
                        name="district"
                        id="district"
                        type="text"
                        placeholder="District"
                        
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <input
                        className="form-control valid"
                        name="community"
                        id="community"
                        type="text"
                        placeholder="Community"
                        
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-group">
                      <textarea
                        className="form-control w-100"
                        name="aboutYourself"
                        id="message"
                        cols="30"
                        rows="9"
                        placeholder="Tell us about yourself & how you qualify for this position"
                        
                      ></textarea>
                    </div>
                  </div>

                  {/* Commented out sections as per your provided code */}
                  {/* <div className="form-group col-12">
                    <label>On which days will you be available for work?</label>
                    <div className="row">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                        <div className="form-check col-sm-4 col-md-3" key={day}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            value={day}
                            id={day}
                            name="availableDays"
                          />
                          <label className="form-check-label" htmlFor={day}>
                            {day.charAt(0).toUpperCase() + day.slice(1)}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div> */}

                  {/* <div className="col-sm-6">
                    <div className="form-group">
                      <label>Languages You Are Fluent In</label>
                      <div className="row">
                        {['twi', 'ga', 'hausa', 'english'].map((lang) => (
                          <div className="form-check col-6" key={lang}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              value={lang}
                              id={lang}
                              name="fluentLanguages"
                            />
                            <label className="form-check-label" htmlFor={lang}>
                              {lang.charAt(0).toUpperCase() + lang.slice(1)}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div> */}

                  <div className="col-sm-6">
                    <div className="form-group">
                      <select
                        className="form-control"
                        name="isPwD"
                        id="isPwD"
                        ref={isPwDRef}
                        onChange={handlePwDChange}
                    
                      >
                        <option value="">Are you a PwD?</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </div>

                  {showDisabilityOptions && (
                    <div className="col-sm-6">
                      <div className="form-group">
                        <select
                          className="form-control"
                          name="disabilityType"
                          id="disabilityType"
                          ref={disabilityTypeRef}
                          onChange={handleDisabilityChange}
                           // This should also be required when visible
                        >
                          <option value="">Select type of disability</option>
                          <option value="visual">Visual impairment</option>
                          <option value="hearing">Hearing impairment</option>
                          <option value="mobility">Mobility impairment</option>
                          <option value="speech">Speech/communication</option>
                          <option value="cognitive">Cognitive/intellectual</option>
                          <option value="learning">Learning disability</option>
                          <option value="mental">Mental health condition</option>
                          <option value="chronic">Chronic illness</option>
                          <option value="neurological">Neurological disorder</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {showOtherField && (
                    <div className="col-sm-6">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          name="otherDisability"
                          id="otherDisability"
                          ref={otherDisabilityRef}
                          placeholder="Please specify the disability type"
                           // This should also be required when visible
                        />
                      </div>
                    </div>
                  )}

                  <div className="col-sm-6">
                    <div className="form-group">
                      <select className="form-control" name="education_level" id="education_level">
                        <option value="">What is your educational level?</option>
                        <option value="none">No formal education</option>
                        <option value="primary">Primary school</option>
                        <option value="junior_high">Junior High School (JHS)</option>
                        <option value="senior_high">Senior High School (SHS)</option>
                        <option value="technical_vocational">Technical/Vocational</option>
                        <option value="diploma">Diploma/HND</option>
                        <option value="undergraduate">Bachelor’s Degree</option>
                        <option value="postgraduate">Master’s Degree</option>
                        <option value="doctorate">Doctorate (PhD)</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="cv">Upload Your CV</label>
                      <input
                        className="form-control-file"
                        type="file"
                        id="cv"
                        name="cv"
                        onChange={handleCVUpload}
                        accept=".pdf,.doc,.docx"
                        required
                      />
                      {uploadProgress > 0 && uploadProgress < 100 && (
                        <div className="mt-2">
                          <p>Uploading: {uploadProgress}%</p>
                          <progress value={uploadProgress} max="100" style={{ width: '100%' }} />
                        </div>
                      )}
                      {cvUploadMessage && <p className={`mt-2 ${cvUploadMessage.includes('successfully') ? 'text-success' : 'text-danger'}`}>{cvUploadMessage}</p>}
                    </div>
                  </div>
                  {submissionError && <div className="col-12 text-danger mt-3">{submissionError}</div>}
                </div>

                <div className="form-group mt-3">
                  <button type="submit" className="button button-contactForm boxed-btn">
                    Submit Application
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Info Section */}
            <div className="col-lg-3 offset-lg-1">
              <div className="media contact-info">
                <span className="contact-info__icon">
                  <i className="ti-home"></i>
                </span>
                <div className="media-body">
                  <h3>Ashanti Region, Ghana.</h3>
                  <p>Ejisu, Model School</p>
                </div>
              </div>
              <div className="media contact-info">
                <span className="contact-info__icon">
                  <i className="ti-tablet"></i>
                </span>
                <div className="media-body">
                  <h3>0249104925</h3>
                  <p>Mon to Fri 8:30am to 5pm</p>
                </div>
              </div>
              <div className="media contact-info">
                <span className="contact-info__icon">
                  <i className="ti-email"></i>
                </span>
                <div className="media-body">
                  <h3>support@jobflowonline.com</h3>
                  <p>Send us your query anytime!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Apply;